// Yggdrasil authentication for Blessing Skin Server
// Authenticates against a custom Yggdrasil API (e.g., Blessing Skin)

const YGGDRASIL_AUTH_SERVER = 'https://skins.nathan.dj/api/yggdrasil/authserver'
const YGGDRASIL_SESSION_SERVER = 'https://skins.nathan.dj/api/yggdrasil/sessionserver'

export interface YggdrasilSession {
  accessToken: string
  clientToken: string
  selectedProfile: {
    id: string
    name: string
  }
  availableProfiles: Array<{
    id: string
    name: string
  }>
}

export async function yggdrasilAuthenticate (email: string, password: string): Promise<YggdrasilSession> {
  const clientToken = crypto.randomUUID().replaceAll('-', '')

  const response = await fetch(`${YGGDRASIL_AUTH_SERVER}/authenticate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      agent: { name: 'Minecraft', version: 1 },
      username: email,
      password,
      clientToken,
      requestUser: true,
    }),
  })

  if (!response.ok) {
    const body = await response.text()
    let errorMessage = `Authentication failed (${response.status})`
    try {
      const json = JSON.parse(body)
      errorMessage = json.errorMessage || json.error || errorMessage
    } catch {}
    throw new Error(errorMessage)
  }

  const data = await response.json()

  if (!data.selectedProfile) {
    throw new Error('No Minecraft profile found. Please create a player on skins.nathan.dj first.')
  }

  return {
    accessToken: data.accessToken,
    clientToken: data.clientToken,
    selectedProfile: data.selectedProfile,
    availableProfiles: data.availableProfiles || [data.selectedProfile],
  }
}

export function getYggdrasilSessionServer (): string {
  return YGGDRASIL_SESSION_SERVER
}

// Show a login dialog and return credentials
export function showYggdrasilLoginDialog (): Promise<{ email: string; password: string } | null> {
  return new Promise((resolve) => {
    // Remove any existing dialog
    const existing = document.getElementById('yggdrasil-login-dialog')
    if (existing) existing.remove()

    const overlay = document.createElement('div')
    overlay.id = 'yggdrasil-login-dialog'
    overlay.style.cssText = `
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.85); z-index: 100000;
      display: flex; align-items: center; justify-content: center;
      font-family: sans-serif;
    `

    const dialog = document.createElement('div')
    dialog.style.cssText = `
      background: #2d2d2d; border-radius: 8px; padding: 32px;
      min-width: 320px; max-width: 400px; color: white;
      box-shadow: 0 8px 32px rgba(0,0,0,0.5);
    `

    dialog.innerHTML = `
      <h2 style="margin: 0 0 8px 0; font-size: 20px; color: #4caf50;">NKraft Login</h2>
      <p style="margin: 0 0 20px 0; font-size: 13px; color: #aaa;">Melde dich mit deinem skins.nathan.dj Konto an</p>
      <div style="margin-bottom: 16px;">
        <label style="display: block; font-size: 12px; color: #ccc; margin-bottom: 4px;">E-Mail</label>
        <input id="ygg-email" type="email" placeholder="email@example.com" autocomplete="email" style="
          width: 100%; padding: 10px; border: 1px solid #555; border-radius: 4px;
          background: #1a1a1a; color: white; font-size: 14px; box-sizing: border-box;
          outline: none;
        " />
      </div>
      <div style="margin-bottom: 20px;">
        <label style="display: block; font-size: 12px; color: #ccc; margin-bottom: 4px;">Passwort</label>
        <input id="ygg-password" type="password" placeholder="Passwort" autocomplete="current-password" style="
          width: 100%; padding: 10px; border: 1px solid #555; border-radius: 4px;
          background: #1a1a1a; color: white; font-size: 14px; box-sizing: border-box;
          outline: none;
        " />
      </div>
      <div id="ygg-error" style="display: none; color: #f44336; font-size: 13px; margin-bottom: 12px;"></div>
      <div style="display: flex; gap: 12px;">
        <button id="ygg-login-btn" style="
          flex: 1; padding: 10px; border: none; border-radius: 4px;
          background: #4caf50; color: white; font-size: 14px; cursor: pointer;
          font-weight: bold;
        ">Anmelden</button>
        <button id="ygg-cancel-btn" style="
          padding: 10px 20px; border: 1px solid #555; border-radius: 4px;
          background: transparent; color: #aaa; font-size: 14px; cursor: pointer;
        ">Abbrechen</button>
      </div>
      <p style="margin: 16px 0 0 0; font-size: 11px; color: #666; text-align: center;">
        Kein Konto? <a href="https://skins.nathan.dj/auth/register" target="_blank" style="color: #4caf50;">Registrieren</a>
      </p>
    `

    overlay.appendChild(dialog)
    document.body.appendChild(overlay)

    const emailInput = document.getElementById('ygg-email') as HTMLInputElement
    const passwordInput = document.getElementById('ygg-password') as HTMLInputElement
    const loginBtn = document.getElementById('ygg-login-btn') as HTMLButtonElement
    const cancelBtn = document.getElementById('ygg-cancel-btn') as HTMLButtonElement
    const errorDiv = document.getElementById('ygg-error') as HTMLDivElement

    emailInput.focus()

    const doLogin = () => {
      const email = emailInput.value.trim()
      const password = passwordInput.value
      if (!email || !password) {
        errorDiv.textContent = 'Bitte E-Mail und Passwort eingeben.'
        errorDiv.style.display = 'block'
        return
      }
      overlay.remove()
      resolve({ email, password })
    }

    loginBtn.addEventListener('click', doLogin)
    passwordInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') doLogin()
    })
    emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') passwordInput.focus()
    })
    cancelBtn.addEventListener('click', () => {
      overlay.remove()
      resolve(null)
    })
  })
}
